import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { clientFetch } from "../utils/clientFetch";

/**
 * 外部連携 API キー管理 (#468 / 管理画面 Phase B) のデータ取得・操作フック。
 *
 * 重要 (受入条件「平文キー1回のみ表示」): create / rotate は **発行レスポンス (full_key 入り)** を
 * そのまま呼び出し元に返す。full_key は発行レスポンスにのみ存在し、一覧再取得では取得できないため、
 * companyContext のような「register → refresh で破棄」パターンを踏襲してはいけない。
 */

const API_BASE = `${process.env.REACT_APP_API_ENDPOINT}/apis/admin`;

export type ExternalApiKeyStatus = "active" | "revoked";
export type ExternalApiKeyStatusFilter = "active" | "revoked" | "all";

export type ExternalApiKeyListItem = {
  id: number;
  key_prefix: string;
  name: string;
  status: ExternalApiKeyStatus;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
  revoked_at: string | null;
};

/** 発行/再発行レスポンス。full_key は一度きり表示用。 */
export type IssuedApiKey = {
  id: number;
  key_prefix: string;
  name: string;
  full_key: string;
  expires_at: string | null;
  created_at: string;
};

export type CreateApiKeyInput = {
  name: string;
  expires_at: string | null;
  allowed_ips: string | null;
};

export type ApiKeyEffectiveLimits = {
  maxApiKeysPerCompany: number;
  allowIpRestriction: boolean;
};

export type ApiKeyPagination = { page: number; limit: number; total: number; total_pages: number };

/** 発行可否: プラン上限が判明していて active 数が上限未満のときのみ true。 */
export function canIssueApiKey(activeCount: number, planLimit: number | null): boolean {
  return planLimit != null && activeCount < planLimit;
}

/** 操作結果 (full_key 入り or エラーメッセージ)。 */
export type IssueResult = { ok: true; key: IssuedApiKey } | { ok: false; message: string };

const DEFAULT_PAGINATION: ApiKeyPagination = { page: 1, limit: 20, total: 0, total_pages: 0 };

async function parseError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return body?.message || `エラーが発生しました (${res.status})`;
  } catch {
    return `エラーが発生しました (${res.status})`;
  }
}

export function useApiKeyContext() {
  const [keys, setKeys] = useState<ExternalApiKeyListItem[]>([]);
  const [pagination, setPagination] = useState<ApiKeyPagination>(DEFAULT_PAGINATION);
  const [limits, setLimits] = useState<ApiKeyEffectiveLimits | null>(null);
  const [activeCount, setActiveCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  /** 一覧取得 (status フィルタ・ページング)。 */
  const refreshKeys = useCallback(
    async (companyId: number, status: ExternalApiKeyStatusFilter, page = 1, limit = 20) => {
      setLoading(true);
      try {
        const q = new URLSearchParams({ companyId: String(companyId), status, page: String(page), limit: String(limit) });
        const res = await clientFetch(`${API_BASE}/external-api-keys?${q.toString()}`, { method: "GET" });
        if (!res.ok) {
          setKeys([]);
          setPagination(DEFAULT_PAGINATION);
          return;
        }
        const body = await res.json();
        setKeys(Array.isArray(body?.data) ? body.data : []);
        setPagination(body?.pagination ?? DEFAULT_PAGINATION);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * プラン上限 (maxApiKeysPerCompany) / IP制限可否 (allowIpRestriction) と active 件数を取得する。
   * 「現在のキー数 / 上限」表示と発行ボタンの活性制御は、テーブルの status フィルタに依存しない
   * **active 件数** で行う必要があるため、ここで status=active の total を別途取得する。
   */
  const refreshLimits = useCallback(async (companyId: number) => {
    // effective-limits
    const res = await clientFetch(`${API_BASE}/companies/${companyId}/effective-limits`, { method: "GET" });
    if (res.ok) {
      const body = await res.json();
      const el = body?.data?.effective_limits;
      if (el && typeof el.maxApiKeysPerCompany === "number") {
        setLimits({ maxApiKeysPerCompany: el.maxApiKeysPerCompany, allowIpRestriction: !!el.allowIpRestriction });
      } else {
        setLimits(null);
      }
    } else {
      setLimits(null);
    }
    // active 件数 (status フィルタ非依存)
    const q = new URLSearchParams({ companyId: String(companyId), status: "active", page: "1", limit: "1" });
    const cntRes = await clientFetch(`${API_BASE}/external-api-keys?${q.toString()}`, { method: "GET" });
    if (cntRes.ok) {
      const body = await cntRes.json();
      setActiveCount(Number(body?.pagination?.total ?? 0));
    } else {
      setActiveCount(0);
    }
  }, []);

  /** 発行。成功時は full_key 入りの IssuedApiKey を返す (呼び出し元が一度きり表示する)。 */
  const createKey = useCallback(async (companyId: number, input: CreateApiKeyInput): Promise<IssueResult> => {
    const res = await clientFetch(`${API_BASE}/external-api-keys`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ company_id: companyId, ...input }),
    });
    if (!res.ok) return { ok: false, message: await parseError(res) };
    const body = await res.json();
    return { ok: true, key: body.data as IssuedApiKey };
  }, []);

  /** 再発行 (rotate)。成功時は新キーの full_key 入り IssuedApiKey を返す。 */
  const rotateKey = useCallback(async (id: number): Promise<IssueResult> => {
    const res = await clientFetch(`${API_BASE}/external-api-keys/${id}/rotate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return { ok: false, message: await parseError(res) };
    const body = await res.json();
    return { ok: true, key: body.data as IssuedApiKey };
  }, []);

  /** 即時失効 (revoke)。成功で true。 */
  const revokeKey = useCallback(async (id: number, reason: string | null): Promise<{ ok: boolean; message?: string }> => {
    const res = await clientFetch(`${API_BASE}/external-api-keys/${id}/revoke`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) return { ok: false, message: await parseError(res) };
    return { ok: true };
  }, []);

  return useMemo(
    () => ({ keys, pagination, limits, activeCount, loading, refreshKeys, refreshLimits, createKey, rotateKey, revokeKey }),
    [keys, pagination, limits, activeCount, loading, refreshKeys, refreshLimits, createKey, rotateKey, revokeKey]
  );
}

type ApiKeyContextValue = ReturnType<typeof useApiKeyContext>;
const ApiKeyContext = createContext<ApiKeyContextValue | null>(null);

export function ApiKeyProvider({ children }: { children: React.ReactNode }) {
  const value = useApiKeyContext();
  return <ApiKeyContext.Provider value={value}>{children}</ApiKeyContext.Provider>;
}

export function useApiKeys(): ApiKeyContextValue {
  const ctx = useContext(ApiKeyContext);
  if (!ctx) throw new Error("useApiKeys must be used within ApiKeyProvider");
  return ctx;
}
